import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { magentoApi } from '../api/magento'
import Boton from '../components/Boton'

/**
 * Pantalla de operación de la integración con la tienda online Magento.
 * - Prueba de conexión (¿la URL + token funcionan?)
 * - "Importar pedidos ahora" (trae los pedidos nuevos como NV borrador)
 * Los pedidos también se importan solos cada 5 min en producción (scheduler).
 */
export default function MagentoPage() {
  const navigate = useNavigate()
  const [estado, setEstado] = useState(null)   // resultado del test de conexión
  const [probando, setProbando] = useState(false)
  const [importando, setImportando] = useState(false)
  const [resultado, setResultado] = useState(null) // resumen de la última importación
  const [error, setError] = useState('')
  // Sección temporal para pruebas (Emiliano / Magento)
  const [debugLoading, setDebugLoading] = useState(false)
  const [debugJson, setDebugJson] = useState(null)
  const [debugError, setDebugError] = useState('')
  const [debugIncrement, setDebugIncrement] = useState('')
  // API custom (api_pedidos / api_productos / api_stock / api_promos)
  const [custom, setCustom] = useState(null)         // { ok, resultados: {endpoint: {ok, error, muestra}} }
  const [probandoCustom, setProbandoCustom] = useState(false)

  const probar = useCallback(async () => {
    setProbando(true); setError('')
    try {
      const { data } = await magentoApi.test()
      setEstado(data)
    } catch (e) {
      setEstado(null)
      setError(e.response?.data?.message || 'No se pudo contactar al servidor.')
    } finally { setProbando(false) }
  }, [])

  const probarCustom = useCallback(async () => {
    setProbandoCustom(true)
    try {
      const { data } = await magentoApi.testCustom()
      setCustom(data)
    } catch (e) {
      setCustom({ ok: false, resultados: {}, error: e.response?.data?.message || 'No se pudo contactar al servidor.' })
    } finally { setProbandoCustom(false) }
  }, [])

  useEffect(() => { probar(); probarCustom() }, [probar, probarCustom])

  const importar = async () => {
    setImportando(true); setError(''); setResultado(null)
    try {
      const { data } = await magentoApi.importarPedidos()
      setResultado(data)
    } catch (e) {
      setError(e.response?.data?.message || 'Error al importar pedidos.')
    } finally { setImportando(false) }
  }

  const traerProcessing = async () => {
    setDebugLoading(true); setDebugError(''); setDebugJson(null)
    try {
      const { data } = await magentoApi.debugProcessing()
      if (data?.ok) setDebugJson(data.respuesta)
      else setDebugError(data?.error || 'No se pudo traer la respuesta.')
    } catch (e) {
      setDebugError(e.response?.data?.message || e.message || 'Error de red.')
    } finally { setDebugLoading(false) }
  }

  const traerPedido = async () => {
    const inc = debugIncrement.trim()
    if (!inc) { setDebugError('Falta el increment_id.'); return }
    setDebugLoading(true); setDebugError(''); setDebugJson(null)
    try {
      const { data } = await magentoApi.debugPedido(inc)
      if (data?.ok) setDebugJson(data.respuesta)
      else setDebugError(data?.error || 'No se pudo traer la respuesta.')
    } catch (e) {
      setDebugError(e.response?.data?.message || e.message || 'Error de red.')
    } finally { setDebugLoading(false) }
  }

  const ok = estado?.ok
  const fake = estado?.respuesta?.fake

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <h2 style={st.title}>Tienda online — Magento</h2>
      <p style={st.sub}>
        Los pedidos de la tienda entran a LGR como <b>Notas de Venta en borrador</b> para que un vendedor
        los revise antes de confirmarlos. Si el pedido vino pagado online, también se registra el cobro.
        Se importan solos cada 5 minutos; acá podés probar la conexión o forzar una importación.
      </p>

      {/* Estado de conexión */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>Conexión</h3>
          <Boton variante="send" sm onClick={probar} disabled={probando}>
            {probando ? 'Probando…' : '↻ Probar conexión'}
          </Boton>
        </div>

        {probando && !estado ? (
          <p style={st.muted}>Verificando…</p>
        ) : ok ? (
          <div style={{ ...st.estadoBox, ...st.estadoOk }}>
            <span style={st.estadoIcon}>✓</span>
            <div>
              <div style={st.estadoTexto}>Conectado correctamente</div>
              {fake && <div style={st.muted}>Modo simulación activo (MAGENTO_FAKE=true): se usan pedidos de ejemplo, no la tienda real.</div>}
            </div>
          </div>
        ) : (
          <div style={{ ...st.estadoBox, ...st.estadoFail }}>
            <span style={st.estadoIcon}>✕</span>
            <div>
              <div style={st.estadoTexto}>Sin conexión con Magento</div>
              <div style={st.muted}>{estado?.error || error || 'Revisá la configuración.'}</div>
              <div style={st.hint}>
                Para conectar: en el admin de Magento → <b>System → Integrations → Add New Integration</b>,
                con permiso de lectura en <b>Sales (Orders)</b>. Copiá la URL de la tienda y el Access Token
                a <code>MAGENTO_URL</code> y <code>MAGENTO_TOKEN</code> en el <code>.env</code> del servidor.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API custom (api_*.php) — reemplazo en validación paralela */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>API custom (X-API-KEY)</h3>
          <Boton variante="send" sm onClick={probarCustom} disabled={probandoCustom}>
            {probandoCustom ? 'Probando…' : '↻ Probar API custom'}
          </Boton>
        </div>
        <p style={st.muted}>
          4 endpoints PHP custom que reemplazan las lecturas del REST nativo (esquivan el WAF de Nexcess).
          Hoy se usan solo si <code>MAGENTO_USE_CUSTOM_API=true</code>; con el flag apagado, LGR sigue
          consumiendo el REST nativo. El cliente cae al REST nativo automáticamente si el custom falla.
        </p>
        {probandoCustom && !custom ? (
          <p style={st.muted}>Verificando los 4 endpoints…</p>
        ) : custom?.error ? (
          <div style={{ ...st.estadoBox, ...st.estadoFail }}>
            <span style={st.estadoIcon}>✕</span>
            <div>
              <div style={st.estadoTexto}>{custom.error}</div>
              <div style={st.muted}>Configurá <code>MAGENTO_CUSTOM_API_KEY</code> en el <code>.env</code> del servidor.</div>
            </div>
          </div>
        ) : custom ? (
          <table style={st.tabla}>
            <thead><tr><th style={st.th}>Endpoint</th><th style={st.th}>Estado</th><th style={st.th}>Detalle</th></tr></thead>
            <tbody>
              {Object.entries(custom.resultados || {}).map(([endpoint, r]) => (
                <tr key={endpoint} style={st.tr}>
                  <td style={st.td}><code style={st.code}>{endpoint}</code></td>
                  <td style={st.td}>
                    {r.ok
                      ? <span style={{ ...st.pill, ...st.pillGreen }}>✓ OK</span>
                      : <span style={{ ...st.pill, ...st.pillRed }}>✕ FAIL</span>}
                  </td>
                  <td style={st.td}>
                    {r.ok
                      ? <span style={st.muted}>{Array.isArray(r.muestra) ? `${r.muestra.length} fila${r.muestra.length === 1 ? '' : 's'} de muestra` : 'OK'}</span>
                      : <span style={{ color: '#991b1b', fontSize: '0.78rem' }}>{r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      {/* Importar pedidos */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>Importar pedidos nuevos</h3>
          <Boton variante="send" onClick={importar} disabled={importando || !ok} title={!ok ? 'Primero hay que tener conexión' : ''}>
            {importando ? 'Importando…' : '⬇ Importar pedidos ahora'}
          </Boton>
        </div>

        {!ok && <p style={st.muted}>Disponible cuando la conexión esté OK.</p>}

        {resultado && (
          <div style={st.resultado}>
            <div style={st.resumenRow}>
              <span style={{ ...st.pill, ...st.pillGreen }}>{resultado.detalle?.importados ?? 0} importados</span>
              <span style={{ ...st.pill, ...st.pillGray }}>{resultado.detalle?.omitidos ?? 0} ya existían</span>
              {(resultado.detalle?.errores?.length ?? 0) > 0 &&
                <span style={{ ...st.pill, ...st.pillRed }}>{resultado.detalle.errores.length} con error</span>}
            </div>

            {resultado.detalle?.notas?.length > 0 && (
              <table style={st.tabla}>
                <thead><tr><th style={st.th}>Pedido Magento</th><th style={st.th}>Nota de Venta</th><th style={st.th}></th></tr></thead>
                <tbody>
                  {resultado.detalle.notas.map(n => (
                    <tr key={n.nv_id} style={st.tr}>
                      <td style={st.td}><code style={st.code}>#{n.pedido}</code></td>
                      <td style={st.td}><code style={st.code}>{n.numero_lgr}</code></td>
                      <td style={st.td}><Boton variante="secondary" sm onClick={() => navigate(`/notas-venta/${n.nv_id}`)}>Ver NV →</Boton></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {resultado.detalle?.errores?.length > 0 && (
              <div style={st.errores}>
                {resultado.detalle.errores.map((e, i) => (
                  <div key={i} style={st.errorLine}>⚠ Pedido #{e.pedido}: {e.error}</div>
                ))}
              </div>
            )}

            {(resultado.detalle?.importados ?? 0) > 0 && (
              <p style={st.muted}>Las nuevas NV quedaron en <b>borrador</b> — revisalas en <a style={st.link} onClick={() => navigate('/notas-venta')}>Notas de Venta</a>.</p>
            )}
            {(resultado.detalle?.importados ?? 0) === 0 && (resultado.detalle?.errores?.length ?? 0) === 0 && (
              <p style={st.muted}>No había pedidos nuevos para importar.</p>
            )}
          </div>
        )}
      </div>

      {error && !estado && <p style={st.errorMsg}>{error}</p>}

      {/* Sección temporal para pruebas con el programador de Magento */}
      <div style={{ ...st.card, border: '1px dashed #f59e0b', background: '#fffbeb' }}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>🧪 Temporal para pruebas</h3>
          <Boton variante="send" sm onClick={traerProcessing} disabled={debugLoading}>
            {debugLoading ? 'Trayendo…' : 'Traer 5 pedidos en Processing'}
          </Boton>
        </div>
        <p style={st.muted}>
          Consulta cruda: <code>GET /rest/V1/orders?status=processing&amp;pageSize=5</code>.
          No importa nada, sólo muestra el JSON tal cual lo manda Magento.
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="increment_id (ej. 000018079)"
            value={debugIncrement}
            onChange={e => setDebugIncrement(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') traerPedido() }}
            style={{ padding: '0.35rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', minWidth: 220 }}
          />
          <Boton variante="send" sm onClick={traerPedido} disabled={debugLoading}>
            {debugLoading ? 'Trayendo…' : 'Traer pedido por número'}
          </Boton>
        </div>

        {debugError && <div style={{ ...st.errorMsg, marginTop: '0.5rem' }}>⚠ {debugError}</div>}

        {debugJson && (
          <pre style={st.jsonBox}>
            {JSON.stringify(debugJson, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}

const st = {
  title:      { margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' },
  sub:        { margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5 },
  card:       { background: 'white', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1.25rem', marginBottom: '1rem' },
  cardHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  cardTitle:  { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' },
  muted:      { color: '#94a3b8', fontSize: '0.85rem', margin: '0.25rem 0' },
  hint:       { color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem', lineHeight: 1.5, background: '#f8fafc', padding: '0.6rem 0.75rem', borderRadius: 6, border: '1px solid #f1f5f9' },
  estadoBox:  { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: 8 },
  estadoOk:   { background: '#dcfce7' },
  estadoFail: { background: '#fee2e2' },
  estadoIcon: { fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.3 },
  estadoTexto:{ fontWeight: 700, color: '#1e293b' },
  resultado:  { marginTop: '0.5rem' },
  resumenRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' },
  pill:       { fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.7rem', borderRadius: 12, textTransform: 'uppercase', letterSpacing: '0.03em' },
  pillGreen:  { background: '#dcfce7', color: '#166534' },
  pillGray:   { background: '#f1f5f9', color: '#475569' },
  pillRed:    { background: '#fee2e2', color: '#991b1b' },
  tabla:      { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th:         { padding: '0.5rem 0.75rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { padding: '0.5rem 0.75rem' },
  code:       { fontFamily: 'monospace', fontSize: '0.8rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 },
  errores:    { marginTop: '0.5rem' },
  errorLine:  { color: '#991b1b', fontSize: '0.82rem', padding: '0.25rem 0' },
  link:       { color: '#1e40af', cursor: 'pointer', textDecoration: 'underline' },
  errorMsg:   { color: '#dc2626', fontSize: '0.875rem' },
  jsonBox:    { marginTop: '0.75rem', background: '#0f172a', color: '#e2e8f0', padding: '0.9rem 1rem', borderRadius: 8, fontSize: '0.78rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', maxHeight: 480, overflow: 'auto', whiteSpace: 'pre', lineHeight: 1.45 },
}
